import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  
  Grid,
  Typography,
} from "@mui/material";
import { Formik, Form, Field, FieldArray, FastField } from "formik";
import * as Yup from "yup";
import { getCatalogue } from "../../api/common/catalogues.ts";
import { getExpenseAccountList } from "../../api/accounting";
import { useSnack } from "../../services/SnakbarProvider";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import DeleteIcon from "@mui/icons-material/Delete";
import { addProduct, getProductById, updateProduct } from "../../api/products";
import { handleError } from "../../services/errorHandler";
import ProductFormHeader from "./ProductFormHeader.tsx";
import ProductFormProperties from "./ProductFormProperties.tsx";
import ProductPaperForm from "./ProductPaperForm.tsx";
import ProductVarnishForm from "./ProductVarnishForm.tsx";
import ProductFoilForm from "./ProductFoilForm.tsx";
import ProductLaminasPlacasForm from "./ProductLaminasPlacasForm.tsx";
import ProductBannersForm from "./ProductBannersForm.tsx";
import ProductCintasForm from "./ProductCintasForm.tsx";
import ProductPegamentosForm from "./ProductPegamentosForm.tsx";
import ProductSolucionesForm from "./ProductSolucionesForm.tsx";
import ProductAccJuguetesForm from "./ProductAccJuguetesForm.tsx";
import ProductTintasForm from "./ProductTintasForm.tsx";
import ProductPlasticosForm from "./ProductPlasticosForm.tsx";
import ProductCartonForm from "./ProductCartonForm.tsx";
import ProductCajaForm from "./ProductCajaForm.tsx";
import ProductOtrosForm from "./ProductOtrosForm.tsx";

const getValidationSchema = (productType) => {
  const baseSchema = {
    originalCode: Yup.string().nullable(),
    productTypeId: Yup.number().required(
      "El campo tipo de producto es obligatorio"
    ),
    productCategoryId: Yup.number().required(
      "El campo categoría de producto es obligatorio"
    ),
    description: Yup.string(),
    expenseAccountId: Yup.number().nullable(),
    props: Yup.array().of(
      Yup.object().shape({
        key: Yup.string().nullable(),
        value: Yup.string().nullable(),
      })
    ),
  };

  return Yup.object().shape(baseSchema);
};

const initialValues = {
  originalCode: "",
  productTypeId: "",
  props: [
    { key: "", value: "" },
    { key: "", value: "" },
    { key: "", value: "" },
    { key: "", value: "" },
    { key: "", value: "" },
  ],
  productCategoryId: "",
  description: "",
  expenseAccountId: null,
};

// Mapping of product types to their corresponding form components
const productTypeFormMap = {
  "PAP": ProductPaperForm,
  "TIN": ProductTintasForm,
  "FOI": ProductFoilForm,
  "CAR": ProductCartonForm,
  "CAJ": ProductCajaForm,
  "BAR": ProductVarnishForm,
  "ACJ": ProductAccJuguetesForm,
  "PLA": ProductPlasticosForm,
  "PEG": ProductPegamentosForm,
  "SOL": ProductSolucionesForm,
  "CIN": ProductCintasForm,
  "LYM": ProductLaminasPlacasForm,
};

const getProductFormComponent = (productType) => {
  const FormComponent = productType ? productTypeFormMap[productType] : null;
  return FormComponent ? <FormComponent /> : <ProductFormProperties />;
};

const ProductForm = ({ id, onSubmit, companyId }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(false);
  const [productType, setProductType] = useState(null);
  const [formData, setFormData] = useState(initialValues);
  const { openSnack } = useSnack();

  const handleSubmit = async (values) => {
    try {
      if (formMode == "insert") {
        let payload = { ...values };
        payload.CompanyId = companyId;
        payload.props = payload.props.reduce((acc, curr) => {
          acc[curr.key] = typeof curr.value === 'string' ? curr.value.toUpperCase() : curr.value;
          return acc;
        }, {});
        const response = await addProduct(payload);
        const productCode = response.data.code;
        
        // Copy product code to clipboard
        try {
          await navigator.clipboard.writeText(productCode);
          openSnack(`Producto guardado satisfactoriamente. Clave del producto (${productCode}) copiada al portapapeles.`, "success");
        } catch (clipboardError) {
          // Fallback if clipboard API fails
          openSnack(`Producto guardado satisfactoriamente. Clave del producto: ${productCode}`, "success");
        }
        
        onSubmit();
      } else if (formMode == "edit") {
        let payload = { ...values };
        payload.props = payload.props.reduce((acc, curr) => {
          acc[curr.key] = curr.value;
          return acc;
        }, {});
        await updateProduct(id, payload);
        openSnack("Producto actualizado satisfactoriamente", "success");
        onSubmit();
      }
    } catch (e) {
      openSnack(handleError(e), "error");
    }

    console.log(values);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getProductById(id);
      const props = Object.entries(res.data.props).map(([key, value]) => ({
        key,
        value,
      }));
      const payload = { ...res.data, props };
      setFormData(payload);
    } catch (e) {
      openSnack(handleError(e), "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (formMode == "edit") {
      fetchData();
    } else {
      setFormData(initialValues);
    }
  }, []);

  return loading ? (
    <CircularProgress />
  ) : (
    <Formik
      initialValues={{ ...formData }}
      validationSchema={getValidationSchema(productType)}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, handleBlur, touched, errors }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="h4">Datos generales del producto</Typography>
              <ProductFormHeader companyId={companyId} setProductType={setProductType} />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h4">Propiedades del producto</Typography>
              {getProductFormComponent(productType)}
            </Grid>
            <Grid item xs={6}>
              <Button variant="contained" type="submit">
                Guardar
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ProductForm;
